/**
 * consolidateProducts.ts — Consolidación de líneas idénticas COMPARTIDA por los motores de
 * impresión (IR webble, ticket Electron y PDF pdfMake).
 *
 * Fusiona líneas de producto EXACTAMENTE iguales — misma llave:
 *   id_producto + nombre + presentacion + precio unitario + instrucciones +
 *   firma ORDENADA de extras (cada extra: id_extra||nombre + cantidad + precio)
 * — SUMANDO cantidades y subtotales, nunca multiplicando: las cantidades y precios de los
 * extras YA vienen extendidos por la cantidad del producto (extras `multiple`).
 *
 * `instrucciones` forma parte de la llave A PROPÓSITO: fusionar un "sin cebolla" con uno
 * normal sería corrupción de pedido. Igual el precio unitario: dos líneas del mismo producto
 * a precios distintos (p.ej. cambio de precio entre agregados) no se tocan.
 *
 * Consolidar aquí (y no solo en el carrito) cubre pedidos históricos al reimprimir y todos
 * los orígenes (menú, teléfono desde el panel, POS). Es idempotente sobre su propia salida:
 * si el carrito ya consolidó, esto queda no-op.
 *
 * Invariante (con test): Σ subtotal (productos y extras) no cambia tras consolidar.
 */

import { ProductExtra, ProductOrder } from '@interfaces/OrderTypes';
import { toFiniteAmount } from '@utils/formats';

// Llave de identidad de un extra dentro de la firma (id_extra con fallback a nombre).
const extraKey = (extra: ProductExtra): string =>
  JSON.stringify([extra.id_extra || extra.nombre, extra.cantidad, extra.precio]);

// Firma ORDENADA de los extras de una línea: mismo multiconjunto ⇒ misma firma.
const extrasSignature = (extras: ProductExtra[] | undefined): string => (extras ?? []).map(extraKey).sort().join('|');

// Llave de fusión de la línea completa.
const mergeKey = (product: ProductOrder): string =>
  JSON.stringify([
    product.id_producto,
    product.nombre,
    product.presentacion ?? '',
    product.precio,
    product.instrucciones ?? '',
    extrasSignature(product.extras),
  ]);

// Extras de una línea ordenados por su llave — la firma idéntica del grupo garantiza que
// las listas ordenadas quedan alineadas 1:1 posición a posición.
const extrasSortedByKey = (extras: ProductExtra[] | undefined): ProductExtra[] =>
  [...(extras ?? [])].sort((a, b) => {
    const ka = extraKey(a);
    const kb = extraKey(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });

// Fusiona un grupo de líneas con la MISMA llave: clona la primera (orden de extras
// preservado) y SUMA cantidades/subtotales de las demás, extra por extra.
const mergeGroup = (group: ProductOrder[]): ProductOrder => {
  if (group.length === 1) return group[0];
  const [first, ...rest] = group;
  const mergedExtras = (first.extras ?? []).map((extra) => ({ ...extra }));
  const merged: ProductOrder = { ...first, extras: mergedExtras };
  // Los clones alineados por llave (la firma idéntica garantiza el 1:1 por posición).
  const alignedClones = extrasSortedByKey(mergedExtras);
  // Coerción por término (TKCN-543/542): el BE puede mandar estos campos como string
  // y `+=` CONCATENARÍA ('540.00' + '135' → '540.00135') en vez de sumar.
  for (const product of rest) {
    merged.cantidad = toFiniteAmount(merged.cantidad) + toFiniteAmount(product.cantidad);
    merged.subtotal = toFiniteAmount(merged.subtotal) + toFiniteAmount(product.subtotal);
    const incoming = extrasSortedByKey(product.extras);
    for (let i = 0; i < alignedClones.length; i++) {
      alignedClones[i].cantidad = toFiniteAmount(alignedClones[i].cantidad) + toFiniteAmount(incoming[i].cantidad);
      alignedClones[i].precio = toFiniteAmount(alignedClones[i].precio) + toFiniteAmount(incoming[i].precio);
      alignedClones[i].subtotal = toFiniteAmount(alignedClones[i].subtotal) + toFiniteAmount(incoming[i].subtotal);
    }
  }
  return merged;
};

/**
 * Consolida líneas idénticas de un pedido para imprimir. No muta la entrada; si no hay nada
 * que fusionar devuelve el arreglo original tal cual (no-op barato).
 *
 * @param {ProductOrder[]} products Líneas de producto del pedido (crudas, sin consolidar).
 * @returns {ProductOrder[]} Líneas consolidadas, cada una en la posición de su primera aparición.
 */
export function consolidateProducts(products: ProductOrder[]): ProductOrder[] {
  if (!Array.isArray(products) || products.length < 2) return products ?? [];
  const groups = new Map<string, ProductOrder[]>();
  for (const product of products) {
    const key = mergeKey(product);
    const group = groups.get(key);
    if (group) group.push(product);
    else groups.set(key, [product]);
  }
  if (groups.size === products.length) return products;
  // Map preserva el orden de inserción ⇒ cada línea fusionada queda en la posición de su
  // PRIMERA aparición.
  return [...groups.values()].map(mergeGroup);
}
